import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperPlane, faXmark, faRobot, faCommentDots,
  faCircle, faBrain
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../i18n/LanguageContext';
import config from '../config';
import { getFallbackReply } from './chatbotFallback';
import './AIChatbot.css';

const AIChatbot = () => {
  const { language, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Welcome message au premier ouvrir
  useEffect(() => {
    if (open && messages.length === 0) {
      setTimeout(() => {
        setMessages([{ role: 'bot', content: t('chatbot.welcome'), id: Date.now() }]);
      }, 300);
    }
  }, [open, messages.length, t]);

  // Auto-scroll vers le bas — uniquement quand le chatbot est ouvert
  // et qu'il y a au moins un message, sinon au mount ça scrolle la page
  // entière jusqu'au bouton flottant.
  useEffect(() => {
    if (open && messages.length > 0 && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [messages, isTyping, open]);

  // Focus input à l'ouverture
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 400);
    }
  }, [open]);

  const sendMessage = async (text) => {
    if (!text || !text.trim()) return;
    const userMsg = { role: 'user', content: text.trim(), id: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setShowQuickActions(false);
    setIsTyping(true);

    try {
      let reply;
      if (config.chatbotWebhook) {
        // Mode production : appel au webhook (n8n, Claude, OpenAI via proxy…)
        const res = await fetch(config.chatbotWebhook, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: text.trim(),
            history: messages.slice(-6).map(m => ({ role: m.role, content: m.content })),
            language,
            source: 'ai-dynamics.fr'
          })
        });
        if (!res.ok) throw new Error('Webhook failed');
        const data = await res.json();
        reply = data.reply || data.message || getFallbackReply(text, language);
      } else {
        // Mode démo : fallback intelligent
        await new Promise(r => setTimeout(r, 600 + Math.random() * 800));
        reply = getFallbackReply(text, language);
      }

      setIsTyping(false);
      setMessages(prev => [...prev, { role: 'bot', content: reply, id: Date.now() + 1 }]);
    } catch (err) {
      setIsTyping(false);
      setMessages(prev => [...prev, { role: 'bot', content: t('chatbot.errorFallback'), id: Date.now() + 1 }]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleQuickAction = (text) => sendMessage(text);

  const quickActions = [
    { key: 'quickNiroprod', text: t('chatbot.quickNiroprod') },
    { key: 'quickTechmill', text: t('chatbot.quickTechmill') },
    { key: 'quickDemo', text: t('chatbot.quickDemo') },
    { key: 'quickPrice', text: t('chatbot.quickPrice') },
    { key: 'quickContact', text: t('chatbot.quickContact') }
  ];

  return (
    <>
      {/* Bouton flottant */}
      <button
        className={`chatbot-btn ${open ? 'chatbot-btn--hidden' : ''}`}
        onClick={() => setOpen(true)}
        aria-label={t('chatbot.openLabel')}
      >
        <FontAwesomeIcon icon={faCommentDots} />
        <span className="chatbot-btn__badge">
          <FontAwesomeIcon icon={faBrain} />
        </span>
      </button>

      {/* Panel de chat */}
      <div className={`chatbot-panel ${open ? 'chatbot-panel--open' : ''}`} role="dialog" aria-label="AI Chat">
        <header className="chatbot-header">
          <div className="chatbot-header__avatar">
            <FontAwesomeIcon icon={faRobot} />
            <span className="chatbot-header__status-dot">
              <FontAwesomeIcon icon={faCircle} />
            </span>
          </div>
          <div className="chatbot-header__info">
            <h3>{t('chatbot.title')} <span className="chatbot-header__badge">IA</span></h3>
            <span className="chatbot-header__subtitle">
              <span className="chatbot-header__online"></span>
              {t('chatbot.status')}
            </span>
          </div>
          <button className="chatbot-header__close" onClick={() => setOpen(false)} aria-label="Close">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </header>

        <div className="chatbot-messages">
          {messages.map(msg => (
            <div key={msg.id} className={`chatbot-msg chatbot-msg--${msg.role}`}>
              {msg.role === 'bot' && (
                <div className="chatbot-msg__avatar"><FontAwesomeIcon icon={faRobot} /></div>
              )}
              <div className="chatbot-msg__bubble">
                {msg.content.split('\n').map((line, i) => <p key={i}>{line}</p>)}
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="chatbot-msg chatbot-msg--bot">
              <div className="chatbot-msg__avatar"><FontAwesomeIcon icon={faRobot} /></div>
              <div className="chatbot-msg__bubble chatbot-msg__bubble--typing">
                <span className="chatbot-typing">
                  <span></span><span></span><span></span>
                </span>
              </div>
            </div>
          )}

          {showQuickActions && messages.length <= 1 && !isTyping && (
            <div className="chatbot-quick">
              <span className="chatbot-quick__label">{t('chatbot.quickActions')}</span>
              <div className="chatbot-quick__buttons">
                {quickActions.map(qa => (
                  <button
                    key={qa.key}
                    type="button"
                    className="chatbot-quick__btn"
                    onClick={() => handleQuickAction(qa.text)}
                  >
                    {qa.text}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <form className="chatbot-input" onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="text"
            placeholder={t('chatbot.placeholder')}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={isTyping}
            maxLength={500}
          />
          <button type="submit" disabled={!input.trim() || isTyping} aria-label={t('chatbot.sendLabel')}>
            <FontAwesomeIcon icon={faPaperPlane} />
          </button>
        </form>

        <footer className="chatbot-footer">
          {t('chatbot.disclaimer')}
          <a href="/contact">{t('chatbot.disclaimerLink')}</a>
        </footer>
      </div>
    </>
  );
};

export default AIChatbot;
